import { Key } from "../constants/enums";
import type { AnalyticsData } from "../types/data";
import type { AppliedFilters, DateRange } from "../constants/config/filter";
import { filterConfigs, getFilterConfig } from "../constants/config/filter";

const isDateInRange = (
  date: string | null,
  range: DateRange,
  includeNotStarted: boolean,
): boolean => {
  // If date is null, include it only if includeNotStarted is true
  if (date === null) {
    return includeNotStarted;
  }
  // Compare using YYYY-MM-DD strings to avoid timezone ambiguities
  const d = date.slice(0, 10);
  const from = range.from.slice(0, 10);
  const to = range.to.slice(0, 10);
  return d >= from && d <= to;
};

const matchesMultiSelect = (
  value: string,
  selectedValues: string[],
): boolean => {
  return selectedValues.length === 0 || selectedValues.includes(value);
};

/**
 * Rewrite id-valued selections that arrived as display names into ids.
 *
 * Links shared before group/lesson filters became id-valued carry names. Doing
 * the swap once, here, keeps every downstream consumer — matching, chips, and
 * the dropdown's checked state — agreed on a single shape. Matching on either
 * shape per row instead would leave the dropdown unable to tick a name-valued
 * selection.
 */
export const normalizeFilterValues = (
  filters: AppliedFilters,
  data: AnalyticsData[],
): AppliedFilters => {
  if (data.length === 0) return filters;

  let next: AppliedFilters | null = null;

  filterConfigs.forEach(({ key, extractValue, extractLabel }) => {
    if (!extractValue || !extractLabel) return;
    const selected = (filters as Record<string, unknown>)[key] as
      | string[]
      | undefined;
    if (!selected?.length) return;

    const ids = new Set<string>();
    const idByLabel = new Map<string, string>();
    data.forEach((row) => {
      const id = String(extractValue(row) ?? "");
      if (!id) return;
      ids.add(id);
      const label = extractLabel(row);
      if (label && !idByLabel.has(label)) idByLabel.set(label, id);
    });

    const mapped = selected.map((value) =>
      ids.has(value) ? value : (idByLabel.get(value) ?? value),
    );
    if (mapped.some((value, i) => value !== selected[i])) {
      next = { ...(next ?? filters), [key]: mapped };
    }
  });

  return next ?? filters;
};

export const applyFilters = (
  data: AnalyticsData[],
  filters: AppliedFilters,
): AnalyticsData[] => {
  const includeNotStarted = filters.includeNotStarted ?? true;

  return data.filter((row) => {
    // Global check: if user excludes Not Started, drop rows with null lastLearned
    if (!includeNotStarted && row[Key.LAST_LEARNED] === null) {
      return false;
    }
    return Object.entries(filters).every(([key, filterValue]) => {
      // Skip the includeNotStarted boolean - it's not a filter field
      if (key === "includeNotStarted") return true;
      if (!filterValue) return true;

      const columnKey = key as Key;
      const config = getFilterConfig(columnKey);
      if (!config || !config.extractValue) return true;

      const rowValue = config.extractValue(row);

      if (Array.isArray(filterValue)) {
        return matchesMultiSelect(String(rowValue ?? ""), filterValue);
      }

      if (
        typeof filterValue === "object" &&
        "from" in filterValue &&
        "to" in filterValue
      ) {
        return isDateInRange(
          rowValue as string | null,
          filterValue as DateRange,
          includeNotStarted,
        );
      }

      return true;
    });
  });
};
